'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { AskAddMoreExams } from './_variants/ask-add-more-exams.component';
import { SearchModalDefault } from './_variants/search-modal-default.component';
import { SelectMoreExams } from './_variants/select-more-exams.component';
import { SelectedOverviewExams } from './_variants/selected-overview-exams.component';
import { ProgressiveSearchModalKeys, RecentSearches, SearchModalProps, SearchResult, SearchType, SelectedProcedures } from './search-modal.types';
import { resolveBackNavigation } from './_utils/resolve-back-navigation.util';

import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import LoadRecentSearchesComponent from '@/app/(main)/(pages)/(autoload)/load-recent-searches.component';
import { initialFilterState } from '@/app/(main)/(pages)/busca/[slug]/_consts/search-filter.consts';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { checkLaboratorialOrVaccineSearch } from '@/app/_utils/check-laboratorial-or-vaccine-search.util';
import { ProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.component';
import { ProgressiveModalEventHandlerProps } from '@/components/ui/progressive-modal/progressive-modal.types';
import { clearPendingModalSeed, openSearchModal, useSearchState } from '@/store/search/search.store';
import { MostSearchedResult } from '@/types/most-search-type';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

export function SearchModal({
  initialSelectedProcedures,
  initialKey,
  locationOverride,
}: SearchModalProps) {
  const { selectedSearch, isSearchModalOpen, pendingModalKey, pendingSelectedProcedures } = useStore(
    useSearchState,
    (state) => ({
      selectedSearch: state?.selectedSearch,
      isSearchModalOpen: state?.isSearchModalOpen,
      pendingModalKey: state?.pendingModalKey,
      pendingSelectedProcedures: state?.pendingSelectedProcedures,
    }),
  );

  const [key, setKey] = useState(initialKey ?? ProgressiveSearchModalKeys.SEARCH);
  const [selectedProcedures, setSelectProcedures] = useState<SelectedProcedures[]>(initialSelectedProcedures || []);
  const [firstProcedureSelected, setFirstProcedureSelected] = useState<SelectedProcedures>({} as SelectedProcedures);
  const [lastSelectedSearch, setLastSelectedSearch] = useState<string | undefined>(selectedSearch?.id);
  const { setFilters } = useSearchFilter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isSearchModalOpen) {
      openSearchModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!pendingModalKey) {
      return;
    }

    setKey(pendingModalKey);
    setSelectProcedures(pendingSelectedProcedures ?? []);
    clearPendingModalSeed();
  }, [pendingModalKey, pendingSelectedProcedures]);

  useEffect(() => {
    if (!selectedSearch?.id || selectedSearch.id === lastSelectedSearch) {
      return;
    }

    setLastSelectedSearch(selectedSearch.id);

    if (!checkLaboratorialOrVaccineSearch(selectedSearch.searchType as SearchType)) {
      setFilters(initialFilterState);
    }
  }, [selectedSearch, lastSelectedSearch, setFilters]);

  function handleSelectProcedure(
    procedure: SelectedProcedures | MostSearchedResult | SearchResult | RecentSearches,
  ): void {
    if (key === ProgressiveSearchModalKeys.SEARCH) {
      onClear();
    }

    if (selectedProcedures.some((item) => item.id === procedure.id)) {
      setSelectProcedures((prev) => prev.filter((item) => item.id !== procedure.id));
      return;
    }

    if (!firstProcedureSelected.id) {
      setFirstProcedureSelected(procedure as SelectedProcedures);
    }

    if ((procedure as RecentSearches).examOrVaccineGroup) {
      const newProcedures = (procedure as RecentSearches).examOrVaccineGroup?.map((item) => ({
        id: item.id!,
        code: item.code!,
        displayName: item.displayName!,
      })) as SelectedProcedures[];

      const uniqueProcedures = newProcedures.filter(
        (newProcedure) => !selectedProcedures.some((selected) => selected.id === newProcedure.id),
      );

      setSelectProcedures((prev) => [...prev, ...uniqueProcedures]);
      return;
    }

    setSelectProcedures((prev) => [
      ...prev,
      {
        id: procedure.id,
        code: procedure.code,
        displayName: procedure.displayName,
      } as SelectedProcedures,
    ]);
  }

  function onClear() {
    setSelectProcedures([]);
    setFirstProcedureSelected({} as SelectedProcedures);
  }

  function onEscapeKeyDown({ event, currentKey, handleJump }: ProgressiveModalEventHandlerProps) {
    const result = resolveBackNavigation(currentKey, 'escape');
    if (!result) {
      return;
    }

    event.preventDefault();

    if (result.action === 'jump') {
      handleJump({ key: result.key, returning: result.returning });
    }
  }

  function onInteractOutside({ event, currentKey, handleJump }: ProgressiveModalEventHandlerProps) {
    event.preventDefault();

    const result = resolveBackNavigation(currentKey, 'interact-outside');
    if (!result) {
      return;
    }

    if (result.action === 'jump') {
      handleJump({ key: result.key, returning: result.returning });
    }
  }

  return (
    <>
      <LoadRecentSearchesComponent />
      {isSearchModalOpen && <TrackUserView eventName={AnalyticsEnum.VIEW_SEARCH_MODAL_DEFAULT} />}

      <ProgressiveModal
        data-testid={DataTestIdEnum.SEARCH_MODAL}
        className="md:max-w-[800px] h-full md:h-[calc(4/6*100vh)]"
        open={isSearchModalOpen}
        hasHeader
        title="Modal de busca"
        description="Busque por procedimentos médicos, especialidades ou profissionais de saúde"
        initialKey={key}
        contentPadding={false}
        handleClose={() => openSearchModal(false)}
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        onOpenAutoFocus={({ event }) => event.preventDefault()}
        onCloseAutoFocus={({ event }) => event.preventDefault()}
        onKeyChange={setKey}
      >
        <SearchModalDefault
          key={ProgressiveSearchModalKeys.SEARCH}
          setOpen={openSearchModal}
          selectLaboratoryProcedure={handleSelectProcedure}
          locationOverride={locationOverride}
        />

        <AskAddMoreExams
          key={ProgressiveSearchModalKeys.ASK_ADD_MORE_EXAMS}
          selectedProcedures={selectedProcedures}
          locationOverride={locationOverride}
        />

        <SelectMoreExams
          key={ProgressiveSearchModalKeys.SELECT_MORE_EXAMS}
          selectedProcedures={selectedProcedures}
          locationOverride={locationOverride}
          onClear={onClear}
          setSelectProcedures={setSelectProcedures}
        />

        <SelectedOverviewExams
          key={ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS}
          selectedProcedures={selectedProcedures}
          handleSelectProcedure={handleSelectProcedure}
          locationOverride={locationOverride}
          onClear={onClear}
        />
      </ProgressiveModal>
    </>
  );
}
